import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Button,
  Form,
  Modal,
  Row,
  Col,
  Spinner,
  Card,
  Badge,
} from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";

import JobApplications from "./JobApplications";
const API_URL = "https://radheyelectrical.com/api/jobposting.php";

const AdminJobPosting = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true); // Initial fetch loader
  const [submitting, setSubmitting] = useState(false); // Form submission loader
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    role: "",
    type: "Full-Time",
    location: "",
    experience: "",
    short_desc: "",
    full_desc: "",
    requirements: "",
    is_active: 1,
  });

  const token = localStorage.getItem("admin_token");

  /* ================= FETCH JOBS ================= */
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}?all=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditMode(false);
    setFormData({
      id: null,
      role: "",
      type: "Full-Time",
      location: "",
      experience: "",
      short_desc: "",
      full_desc: "",
      requirements: "",
      is_active: 1,
    });
    setShowModal(true);
  };

  const openEditModal = (job) => {
    setEditMode(true);
    setFormData({
      ...job,
      requirements: Array.isArray(job.requirements)
        ? job.requirements.join(", ")
        : job.requirements,
    });
    setShowModal(true);
  };

  /* ================= SAVE JOB ================= */
  const handleSubmit = async () => {
    setSubmitting(true);
    const payload = {
      ...formData,
      requirements: formData.requirements.split(",").map((r) => r.trim()),
    };

    try {
      const method = editMode ? "PUT" : "POST";
      const res = await fetch(API_URL, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setShowModal(false);
        fetchJobs();
      }
    } catch (err) {
      console.error("Save Error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this job posting?"))
      return;

    try {
      await fetch(`${API_URL}?id=${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchJobs();
    } catch (err) {
      console.error("Delete Error:", err);
    }
  };

  return (
    <>
      <Tabs
        defaultActiveKey="profile"
        id="uncontrolled-tab-example"
        className="mb-3"
      >
        <Tab eventKey="home" title="Job Management">
          <>
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                <Button
                  variant="primary"
                  onClick={openAddModal}
                  className="px-4 shadow-sm"
                >
                  <span className="me-1">+</span> Create New Job
                </Button>
              </Card.Header>

              <Card.Body>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner
                      animation="border"
                      variant="primary"
                      role="status"
                    />
                    <p className="mt-2 text-muted">Loading postings...</p>
                  </div>
                ) : (
                  <Table responsive hover className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th>Role & Type</th>
                        <th>Location</th>
                        <th>Experience</th>
                        <th>Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.length > 0 ? (
                        jobs.map((job) => (
                          <tr key={job.id}>
                            <td>
                              <div className="fw-bold">{job.role}</div>
                              <small className="text-muted">{job.type}</small>
                            </td>
                            <td>{job.location}</td>
                            <td>{job.experience}</td>
                            <td>
                              <Badge
                                bg={job.is_active ? "success" : "secondary"}
                              >
                                {job.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Button
                                size="sm"
                                variant="outline-warning"
                                onClick={() => openEditModal(job)}
                                className="me-2"
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDelete(job.id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="5"
                            className="text-center py-4 text-muted"
                          >
                            No job postings found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            {/* ================= MODAL ================= */}
            <Modal
              show={showModal}
              onHide={() => !submitting && setShowModal(false)}
              size="lg"
              centered
            >
              <Modal.Header closeButton={!submitting}>
                <Modal.Title className="fw-bold">
                  {editMode ? "📝 Edit Job Posting" : "🚀 Create Job Posting"}
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <Form>
                  <Row className="mb-3">
                    <Form.Group as={Col} md={6}>
                      <Form.Label className="fw-semibold">Job Role</Form.Label>
                      <Form.Control
                        placeholder="e.g. Senior Electrician"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group as={Col} md={6}>
                      <Form.Label className="fw-semibold">Job Type</Form.Label>
                      <Form.Select
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                      >
                        <option value="Full-Time">Full-Time</option>
                        <option value="Part-Time">Part-Time</option>
                        <option value="Contract">Contract</option>
                        <option value="Internship">Internship</option>
                      </Form.Select>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group as={Col} md={6}>
                      <Form.Label className="fw-semibold">Location</Form.Label>
                      <Form.Control
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group as={Col} md={6}>
                      <Form.Label className="fw-semibold">
                        Experience Level
                      </Form.Label>
                      <Form.Control
                        placeholder="e.g. 2-5 Years"
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      Short Summary
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="short_desc"
                      value={formData.short_desc}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      Full Job Description
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="full_desc"
                      value={formData.full_desc}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold text-primary">
                      Requirements
                    </Form.Label>
                    <Form.Control
                      placeholder="Separate skills with commas (e.g. React, Node, SQL)"
                      name="requirements"
                      value={formData.requirements}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Check
                    type="switch"
                    id="active-switch"
                    label="Publish Job (Visible to public)"
                    checked={formData.is_active === 1}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_active: e.target.checked ? 1 : 0,
                      })
                    }
                  />
                </Form>
              </Modal.Body>

              <Modal.Footer className="bg-light">
                <Button
                  variant="link"
                  className="text-muted"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-4"
                >
                  {submitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : editMode ? (
                    "Update Posting"
                  ) : (
                    "Post Job"
                  )}
                </Button>
              </Modal.Footer>
            </Modal>
          </>
        </Tab>
        <Tab eventKey="profile" title="Job Application">
          <JobApplications />
        </Tab>
      </Tabs>
    </>
  );
};

export default AdminJobPosting;
